import React, { useState } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config';
import { ValidateCredentialsRequest, ValidateCredentialsResponse } from '../types';

interface UserInputProps {
  onSubmit: (userName: string) => void;
  isLoading?: boolean;
  onNewSession?: () => void;
  showNewSessionButton?: boolean;
}

const UserInput: React.FC<UserInputProps> = ({ 
  onSubmit, 
  isLoading = false, 
  onNewSession,
  showNewSessionButton = false 
}) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (username.trim() && password.trim()) {
      setIsValidating(true);
      setError('');
      setSuccessMessage('');
      
      try {
        const request: ValidateCredentialsRequest = {
          username: username.trim(),
          password: password.trim()
        };
        
        const response = await axios.post(
          buildApiUrl('/api/interview/auth/validate'),
          request
        );
        
        if (response.data.success) {
          setSuccessMessage(response.data.message);
          // Start interview with validated user
          onSubmit(username.trim());
        } else {
          setError(response.data.message);
        }
      } catch (err: any) {
        console.error('Authentication error:', err);
        setError(err.response?.data?.detail || 'Failed to validate credentials. Please try again.');
      } finally {
        setIsValidating(false);
      }
    }
  };

  return (
    <div className="max-w-md min-w-[600px] mx-auto bg-white rounded-lg shadow-md p-12">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 pb-4">
          Welcome to the Interview
        </h2>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
            Username *
          </label>
          <input
            type="text"
            id="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter your username"
            required
            disabled={isValidating || isLoading}
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
            Password *
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter your password"
            required
            disabled={isValidating || isLoading}
          />
        </div>



        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-md p-3">
            <p className="text-green-800 text-sm">{successMessage}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={!username.trim() || !password.trim() || isValidating || isLoading}
          className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isValidating ? 'Validating...' : isLoading ? 'Starting Interview...' : 'Start Interview'}
        </button>
      </form>

    
    </div>
  );
};

export default UserInput; 